/**
 * Accidental-edit guard, not authorization. The version-aware database writer
 * must independently compare expectedRevision and enforce access. A used entry
 * gets two separate affirmative actions, bound to the whole displayed proposal.
 */
export type CopyEditFields = Readonly<Record<string, string | null>>;

export interface CopyEditBaseline {
  readonly id: string;
  readonly revision: string;
  readonly used: boolean;
  readonly fields: CopyEditFields;
}

export interface CopyEditCommand {
  readonly copySetId: string;
  readonly expectedRevision: string;
  readonly fields: CopyEditFields;
  readonly requestId: string;
  readonly confirmedUsedEdit: boolean;
}

export type CopyEditPhase = 'editing' | 'first' | 'second' | 'ready' | 'saving' | 'conflict';

export interface CopyEditState {
  readonly baseline: CopyEditBaseline;
  readonly proposal: CopyEditFields;
  readonly phase: CopyEditPhase;
  readonly command: CopyEditCommand | null;
}

function copyFields(fields: CopyEditFields): CopyEditFields {
  // Copy values rather than retaining a form object's mutable reference.
  return Object.freeze({ ...fields });
}

function sameFields(left: CopyEditFields, right: CopyEditFields): boolean {
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => left[key] === right[key]);
}

export function copyWordingChanged(saved: CopyEditFields, proposed: CopyEditFields): boolean {
  return [
    'name',
    'channel',
    'type',
    ...Array.from({ length: 8 }, (_, i) => [`e${i + 1}_subject`, `e${i + 1}_body`]).flat(),
  ].some((key) => saved[key] !== proposed[key]);
}

export function beginCopyEdit(baseline: CopyEditBaseline): CopyEditState {
  if (baseline.id.trim() === '' || baseline.revision.trim() === '') {
    throw new Error('copy_edit_revision_required');
  }
  const saved = Object.freeze({ ...baseline, fields: copyFields(baseline.fields) });
  return { baseline: saved, proposal: saved.fields, phase: 'editing', command: null };
}

/** Any changed proposal invalidates BOTH confirmations and its retry identity. */
export function proposeCopyEdit(state: CopyEditState, fields: CopyEditFields): CopyEditState {
  if (state.phase === 'saving' || state.phase === 'conflict') return state;
  if (sameFields(state.proposal, fields)) return state;
  return { ...state, proposal: copyFields(fields), phase: 'editing', command: null };
}

/** Prepare only. A used wording edit does not produce a writable command here. */
export function requestCopyEdit(state: CopyEditState, requestId: string): CopyEditState {
  if (state.phase !== 'editing' || requestId.trim() === '') return state;
  if (sameFields(state.baseline.fields, state.proposal)) return state;
  if (state.baseline.used && copyWordingChanged(state.baseline.fields, state.proposal)) {
    return { ...state, phase: 'first', command: null };
  }
  return readyCopyEdit(state, requestId, false);
}

/** First dialog's affirmative action only opens the second dialog. */
export function confirmCopyEditFirst(state: CopyEditState): CopyEditState {
  return state.phase === 'first' ? { ...state, phase: 'second' } : state;
}

/** Second action creates one command. Repeated clicks keep its original UUID. */
export function confirmCopyEditSecond(state: CopyEditState, requestId: string): CopyEditState {
  if (state.phase !== 'second' || requestId.trim() === '') return state;
  return readyCopyEdit(state, requestId, true);
}

function readyCopyEdit(state: CopyEditState, requestId: string, confirmed: boolean): CopyEditState {
  return {
    ...state,
    phase: 'ready',
    command: Object.freeze({
      copySetId: state.baseline.id,
      expectedRevision: state.baseline.revision,
      // CAS binds the complete displayed baseline. Send only actual changes:
      // untouched nullable legacy slots must not become empty strings merely
      // because the editor displays both as blank.
      fields: copyFields(
        Object.fromEntries(
          Object.entries(state.proposal).filter(
            ([key, value]) => value !== state.baseline.fields[key],
          ),
        ),
      ),
      requestId,
      confirmedUsedEdit: confirmed,
    }),
  };
}

export function cancelCopyEditConfirmation(state: CopyEditState): CopyEditState {
  if (state.phase === 'saving' || state.phase === 'conflict') return state;
  return { ...state, phase: 'editing', command: null };
}

/** Begin a write only with the exact proposal and displayed revision confirmed. */
export function startCopyEditSave(state: CopyEditState, displayedRevision: string): CopyEditState {
  if (state.phase !== 'ready' || state.command === null) return state;
  if (displayedRevision !== state.baseline.revision) return invalidateCopyEdit(state);
  return { ...state, phase: 'saving' };
}

/** Response-loss retries reuse the SAME command; they do not create a new edit. */
export function retryCopyEditSave(state: CopyEditState): CopyEditState {
  return state.phase === 'saving' ? { ...state, phase: 'ready' } : state;
}

/** A database CAS refusal requires reload, then two fresh confirmations. */
export function invalidateCopyEdit(state: CopyEditState): CopyEditState {
  return { ...state, phase: 'conflict', command: null };
}

export function reloadCopyEdit(state: CopyEditState, baseline: CopyEditBaseline): CopyEditState {
  if (baseline.id !== state.baseline.id) throw new Error('copy_edit_wrong_entry');
  if (state.phase === 'saving') return state;
  // Keep the unsaved proposal, not the old revision's permission to save it.
  return { ...beginCopyEdit(baseline), proposal: state.proposal };
}
